import { Injectable, inject, signal, computed } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { ComponentType } from '@angular/cdk/portal';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface ResponsiveDialogConfig extends MatDialogConfig {
  /** Custom width for desktop (default: '600px') */
  desktopWidth?: string;
  /** Custom width for tablet (default: '80vw') */
  tabletWidth?: string;
  /** Custom width for mobile (default: fullscreen) */
  mobileWidth?: string;
  /** Whether to use fullscreen on mobile (default: true) */
  mobileFullscreen?: boolean;
  /** Custom panel class prefix (will add -mobile, -tablet, -desktop suffix) */
  panelClassPrefix?: string;
}

type DeviceType = 'mobile' | 'tablet' | 'desktop';

interface BreakpointState {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  deviceType: DeviceType;
}

@Injectable({
  providedIn: 'root'
})
export class ResponsiveDialogService {
  private dialog = inject(MatDialog);
  private breakpointObserver = inject(BreakpointObserver);

  // ✅ Private signal for internal use
  private _breakpointState = toSignal(
    this.breakpointObserver.observe([
      Breakpoints.Handset,
      Breakpoints.Tablet,
      Breakpoints.Web
    ]).pipe(
      map(result => {
        const isMobile = result.breakpoints[Breakpoints.Handset] || false;
        const isTablet = result.breakpoints[Breakpoints.Tablet] || false;
        const isDesktop = !isMobile && !isTablet;
        
        const deviceType: DeviceType = isMobile ? 'mobile' : 
                                        isTablet ? 'tablet' : 
                                        'desktop';
        
        return {
          isMobile,
          isTablet,
          isDesktop,
          deviceType
        } as BreakpointState;
      })
    ),
    { 
      initialValue: {
        isMobile: false,
        isTablet: false,
        isDesktop: true,
        deviceType: 'desktop' as DeviceType
      }
    }
  );

  /**
   * Opens a dialog with responsive configuration based on device type
   * 
   * Mobile: Fullscreen dialog with slide-up animation
   * Tablet: 80vw width with rounded corners
   * Desktop: Fixed width (default 600px) with elevation
   * 
   * @param component The component to display in the dialog
   * @param config Optional responsive dialog configuration
   * @returns MatDialogRef instance
   */
  open<T, D = any, R = any>(
    component: ComponentType<T>,
    config?: ResponsiveDialogConfig
  ): MatDialogRef<T, R> {
    const responsiveConfig = this.getResponsiveConfig(config);
    return this.dialog.open(component, responsiveConfig);
  }

  /**
   * Opens a dialog with responsive configuration and returns an observable
   * that completes when the dialog is closed
   * 
   * @param component The component to display in the dialog
   * @param config Optional responsive dialog configuration
   * @returns Observable that emits the dialog result
   */
  openAndWaitForClose<T, R = any>(
    component: ComponentType<T>,
    config?: ResponsiveDialogConfig
  ): Observable<R | undefined> {
    const dialogRef = this.open<T, any, R>(component, config);
    return dialogRef.afterClosed();
  }

  /**
   * Gets the appropriate dialog configuration based on current viewport
   */
  private getResponsiveConfig(config?: ResponsiveDialogConfig): MatDialogConfig {
    // ✅ Get current breakpoint state from signal (always fresh)
    const state = this._breakpointState();
    
    // Default values
    const desktopWidth = config?.desktopWidth || '600px';
    const tabletWidth = config?.tabletWidth || '80vw';
    const mobileFullscreen = config?.mobileFullscreen ?? true;
    const panelClassPrefix = config?.panelClassPrefix || 'responsive-dialog';

    // Base configuration
    const baseConfig: MatDialogConfig = {
      hasBackdrop: true,
      disableClose: false,
      autoFocus: true,
      restoreFocus: true,
      ...config
    };

    // ✅ Helper to merge panel classes safely
    const mergePanelClasses = (...classes: string[]): string[] => {
      const existingClasses = Array.isArray(config?.panelClass) 
        ? config.panelClass 
        : config?.panelClass 
          ? [config.panelClass] 
          : [];
      
      return [...classes, ...existingClasses];
    };

    // Mobile configuration - fullscreen by default
    if (state.isMobile) {
      if (mobileFullscreen) {
        return {
          ...baseConfig,
          width: '100vw',
          height: '100vh',
          maxWidth: '100vw',
          maxHeight: '100vh',
          panelClass: mergePanelClasses(
            `${panelClassPrefix}-mobile`,
            'fullscreen-dialog'
          )
        };
      } else {
        return {
          ...baseConfig,
          width: config?.mobileWidth || '95vw',
          maxWidth: '95vw',
          maxHeight: '90vh',
          panelClass: mergePanelClasses(
            `${panelClassPrefix}-mobile`,
            'mobile-dialog'
          )
        };
      }
    }

    // Tablet configuration
    if (state.isTablet) {
      return {
        ...baseConfig,
        width: tabletWidth,
        maxWidth: '90vw',
        maxHeight: '85vh',
        panelClass: mergePanelClasses(
          `${panelClassPrefix}-tablet`,
          'tablet-dialog'
        )
      };
    }

    // Desktop configuration
    return {
      ...baseConfig,
      width: desktopWidth,
      maxWidth: '90vw',
      maxHeight: '90vh',
      panelClass: mergePanelClasses(
        `${panelClassPrefix}-desktop`,
        'desktop-dialog'
      )
    };
  }

  readonly isMobile = computed(() => this._breakpointState().isMobile);
  readonly isTablet = computed(() => this._breakpointState().isTablet);
  readonly isDesktop = computed(() => this._breakpointState().isDesktop);
  readonly deviceType = computed(() => this._breakpointState().deviceType);

  /**
   * Gets the current device type
   * @returns 'mobile' | 'tablet' | 'desktop'
   */
  getDeviceType(): DeviceType {
    return this._breakpointState().deviceType;
  }
}